import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import API from 'api';
import {getSignedUrl, getScale} from 'utils';
import {usesTouchActivityLayout} from './touch-layout';
import {usersKey} from 'config';
import {isMobileLocalFriend, updateMobileFamilyFriend} from 'utils/mobileFamilyFriends';
import {closeWritingFeedback} from 'actions/flinkPlayActions';
import useGetStringAudio from 'utils/hooks/useGetStringAudio';
import {StoryStudio} from './MathRodsStoryStudio';
import {WritingAssistant} from './MathRodsWritingAssistant';
import {mathRodsString} from './strings.mjs';
import {writingIdentity, writingActivityFor, chooseWritingDocument, captureWritingWorkspace} from './writing-model.mjs';

const clone = value => JSON.parse(JSON.stringify(value));
async function loadWriting(learnerId, storageActivityId) {
  const url = await getSignedUrl({key:`${usersKey}/${learnerId}/${storageActivityId}/data.json`, method:'get', uploadBucket:true});
  const response = await fetch(url, {cache:'no-store'});
  if (response.status === 404) return null;
  if (!response.ok) throw Error('The saved writing could not be read. It has not been replaced.');
  return response.json();
}
// Reuse both complete native writing screens. Only their storage/lifecycle adapter
// differs: the parent activity segment owns the document and completion/reporting.
function attachSegmentDocument(instance, props) {
  instance.state = {...instance.state, data:clone(props.session.document), learnerAssetsList:[],
    initialSave:false, needToCopyAllImages:false, hasUnsavedChanges:false,
    ...(props.session.kind==='story-studio'?{pagesCount:Math.max(1,props.session.document.pages?.length||0),activePageIdx:Math.max(0,(props.session.document.pages?.length||1)-1)}:{}),
    paths:{...instance.state.paths, activityAssetsFolder:'', learnerAssetsFolder:''}};
  instance.getLearnerData = () => {}; // Loaded before mount; never read/write the parent activity's data.json.
  instance.getLearnerAssets = () => {};
  const originalUnmount=instance.componentWillUnmount?.bind(instance);
  instance.componentWillUnmount=()=>{instance.writingUnmounted=true;originalUnmount?.();};
  instance.writingSaveChain=Promise.resolve();
  instance.saveChanges = callback => {
    const document=clone(instance.state.data);
    instance.writingSaveChain=instance.writingSaveChain.then(async()=>{
      await instance.props.onSave(document);
      if(!instance.writingUnmounted)instance.setState({hasUnsavedChanges:false,initialSave:false});
      if(callback)callback();
    }).catch(()=>{if(!instance.writingUnmounted)instance.setState({exiting:false});instance.closingWriting=false;});
    return instance.writingSaveChain;
  };
  instance.onExit = () => {
    if(instance.closingWriting)return;instance.closingWriting=true;
    instance.props.closeWritingFeedback();
    instance.setState({exiting:true}, () => instance.saveChanges(() => instance.props.onReturn(clone(instance.state.data))));
  };
}
class SegmentStoryStudio extends StoryStudio {
  constructor(props) { super(props); attachSegmentDocument(this, props); }
}
class SegmentWritingAssistant extends WritingAssistant {
  constructor(props) { super(props); attachSegmentDocument(this, props); }
}

export function useMathRodsWriting({activity, language = 'en', translate}) {
  const dispatch=useDispatch();
  const runtime=useSelector(({flinkPlay,game,status})=>({learner:flinkPlay.learner,team:flinkPlay.team,
    noReports:!!flinkPlay.learningCenter?.config?.noReports,preview:!!game.isPreview,
    solutionLocale:flinkPlay.solutionLocale,aspectRatio:status.aspectRatio}));
  const getStringAudio=useGetStringAudio(language);
  const [session,setSession]=useState(null),[busy,setBusy]=useState(false),[error,setError]=useState('');
  const drafts=useRef(new Map()),saved=useRef(new Map()),pendingOpen=useRef(new Map()),alive=useRef(true),working=useRef(false),scopeVersion=useRef(0);
  const activityId=activity?.activity?._id || activity?._id;
  useEffect(()=>{alive.current=true;scopeVersion.current++;working.current=false;drafts.current.clear();saved.current.clear();pendingOpen.current.clear();setSession(null);setBusy(false);setError('');const versionRef=scopeVersion;return()=>{alive.current=false;versionRef.current++;};},[activityId,runtime.learner?._id]);
  const persistent=!!runtime.learner?._id&&!runtime.preview&&!runtime.noReports;
  const learnerIds=useMemo(()=>[...new Set([runtime.learner?._id,...(runtime.team||[]).filter(member=>!isMobileLocalFriend(member)).map(member=>member._id)].filter(Boolean))],[runtime.learner?._id,runtime.team]);
  const persist=useCallback(async (target,document)=>{
    if(target.scopeVersion!==scopeVersion.current)return;
    const value=clone(document),signature=JSON.stringify(value);
    drafts.current.set(target.key,value);
    if(persistent&&saved.current.get(target.key)!==signature){
      const success=await API.game.saveLearnerActivityData({learnerId:learnerIds,activityId:target.storageActivityId,data:value});
      if(!success)throw Error('Writing could not be saved. Please try Go Back again; your work is still open.');
      if(target.scopeVersion!==scopeVersion.current)return;
      saved.current.set(target.key,signature);
      for(const friend of (runtime.team||[]).filter(isMobileLocalFriend))updateMobileFamilyFriend(friend._id,{activityData:{...(friend.activityData||{}),[target.storageActivityId]:value}});
    }
  },[persistent,learnerIds,runtime.team]);
  const open=useCallback(async snapshot=>{
    if(working.current||session||snapshot?.hasLearnerObjects!==true)return;
    const version=scopeVersion.current;
    working.current=true;setBusy(true);setError('');
    try{
      const identity=writingIdentity({activityId,segmentId:snapshot.segmentId,language:snapshot.language||language,writingActivity:snapshot.writingActivity});
      let document=pendingOpen.current.get(identity.key);
      if(!document){
        // Start capture synchronously on the click, before a busy render can
        // dim the board or a storage request can delay reading its geometry.
        const image=await (snapshot.capture?snapshot.capture():captureWritingWorkspace(snapshot,{tens:mathRodsString(translate,4084,'Tens'),ones:mathRodsString(translate,4085,'Ones')}));
        if(!alive.current||version!==scopeVersion.current)return;
        const cached=drafts.current.get(identity.key);
        const stored=cached===undefined&&persistent?await loadWriting(runtime.learner._id,identity.storageActivityId):null;
        if(!alive.current||version!==scopeVersion.current)return;
        document=chooseWritingDocument({kind:identity.kind,cached,stored,capture:()=>image});
        // A failed save retry writes this same document instead of appending the
        // same snapshot twice. A successful deliberate reopen captures again.
        pendingOpen.current.set(identity.key,document);
      }
      const next={...identity,document,scopeVersion:version,segmentId:snapshot.segmentId};
      await persist(next,document);
      if(alive.current&&version===scopeVersion.current){pendingOpen.current.delete(identity.key);setSession(next);}
    }catch(problem){if(alive.current&&version===scopeVersion.current)setError(problem.message);}
    finally{if(version===scopeVersion.current){working.current=false;if(alive.current)setBusy(false);}}
  },[activityId,language,persistent,runtime.learner?._id,persist,session,translate]);
  const onSave=async document=>{if(session.scopeVersion!==scopeVersion.current)return;setError('');try{await persist(session,document);}catch(problem){if(session.scopeVersion===scopeVersion.current)setError(problem.message);throw problem;}};
  const onReturn=document=>{if(session.scopeVersion!==scopeVersion.current)return;drafts.current.set(session.key,clone(document));setError('');setSession(null);};
  const screen=session?<MathRodsWriting session={session} activity={activity} language={language}
    translate={translate} getStringAudio={getStringAudio} learnerId={persistent?runtime.learner._id:null}
    team={persistent?(runtime.team||[]).filter(member=>!isMobileLocalFriend(member)):[]} preview={!persistent} noReports={runtime.noReports}
    solutionLocale={{...runtime.solutionLocale,code:language}} aspectRatio={runtime.aspectRatio}
    closeWritingFeedback={()=>dispatch(closeWritingFeedback())} onSave={onSave} onReturn={onReturn} error={error}/>:null;
  return {open,screen,active:!!session,busy,error};
}
export default function MathRodsWriting({activity,session,error,...props}) {
  const [layoutScale,setLayoutScale]=useState(getScale);
  const mobileLayout=usesTouchActivityLayout();
  useEffect(()=>{const resize=()=>setLayoutScale(getScale());window.addEventListener('resize',resize);return()=>window.removeEventListener('resize',resize);},[]);
  const preserveImageSize=session.document.workspaceCapture?.version >= 2;
  const Component=session.kind==='writing-assistant'?SegmentWritingAssistant:SegmentStoryStudio;
  return <div className={`math-rods-writing-screen${mobileLayout?' math-rods-writing-mobile':''}${preserveImageSize?' math-rods-writing-original-image':''}`} role="region" data-writing-segment={session.segmentId} aria-label={session.kind==='writing-assistant'?'Writing Assistant':'Story Studio'}
    style={{position:'absolute',inset:0,zIndex:10,'--math-rods-writing-scale':layoutScale}}>
    <Component {...props} key={session.key} session={session} activity={writingActivityFor(activity,session)}
      workspaceSnapshots reserveImageList dividedImageBox separateWorkspaceImage={mobileLayout} preserveCurrentImageSize={preserveImageSize} mobileLayout={mobileLayout} showStats={()=>{}} setActivityMastered={()=>{}} setActivityPartial={()=>{}}/>
    {error&&<p role="alert" style={{position:'absolute',top:0,left:'10%',right:'10%',padding:12,background:'#fff4e5',color:'#5a2700',zIndex:20}}>{error}</p>}
  </div>;
}
